import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = '/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('ebd_token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

const getMesAtual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const getMesLabel = (mes) => {
  const [ano, m] = mes.split('-');
  const meses = ['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  return `${meses[parseInt(m)]} ${ano}`;
};

const navegarMes = (mesAtual, delta) => {
  const [ano, m] = mesAtual.split('-').map(Number);
  let novoMes = m + delta;
  let novoAno = ano;
  if (novoMes < 1) { novoMes = 12; novoAno--; }
  if (novoMes > 12) { novoMes = 1; novoAno++; }
  return `${novoAno}-${String(novoMes).padStart(2, '0')}`;
};

const ContasReceber = ({ setCurrentView }) => {
  const [contas, setContas] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('contas'); // contas | historico
  const [message, setMessage] = useState({ type: '', text: '' });
  const [registrando, setRegistrando] = useState(null);
  const [formManual, setFormManual] = useState({ metodo: 'manual', observacao: '', valor: '' });
  const [filtroHistorico, setFiltroHistorico] = useState('');
  const [mesSelecionado, setMesSelecionado] = useState(getMesAtual());

  useEffect(() => {
    loadData(mesSelecionado);
  }, [mesSelecionado]);

  const loadData = async (mes) => {
    setLoading(true);
    try {
      const [contasRes, histRes] = await Promise.all([
        axios.get(`${API}/pagamentos/contas-receber?mes=${mes}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/pagamentos/historico?mes=${mes}`, { headers: getAuthHeaders() }),
      ]);
      setContas(contasRes.data);
      setHistorico(histRes.data);
    } catch (err) {
      setMessage({ type: 'error', text: 'Erro ao carregar dados: ' + (err.response?.data?.detail || err.message) });
    }
    setLoading(false);
  };

  const handleRegistrarPagamento = async (igreja) => {
    const valor = formManual.valor ? parseFloat(formManual.valor) : null;

    try {
      const res = await axios.post(`${API}/pagamentos/registrar-manual`, {
        igreja_id: igreja.igreja_id,
        valor: valor,
        metodo: formManual.metodo,
        observacao: formManual.observacao || null,
        referencia_mes: mesSelecionado,
      }, { headers: getAuthHeaders() });

      setMessage({ type: 'success', text: res.data.message });
      setRegistrando(null);
      setFormManual({ metodo: 'manual', observacao: '', valor: '' });
      loadData(mesSelecionado);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Erro ao registrar pagamento' });
    }
  };

  const statusConfig = {
    em_atraso: { bg: 'bg-red-100 text-red-700 border-red-200', badge: 'bg-red-500', label: 'Em Atraso' },
    pendente: { bg: 'bg-amber-50 text-amber-700 border-amber-200', badge: 'bg-amber-500', label: 'Pendente' },
    pago: { bg: 'bg-green-50 text-green-700 border-green-200', badge: 'bg-green-500', label: 'Pago' },
    em_dia: { bg: 'bg-blue-50 text-blue-700 border-blue-200', badge: 'bg-blue-500', label: 'Em Dia' },
    sem_vencimento: { bg: 'bg-gray-50 text-gray-600 border-gray-200', badge: 'bg-gray-400', label: 'Sem Venc.' },
  };

  const metodoLabels = {
    manual: 'Manual', pix: 'PIX', cartao: 'Cartão', deposito: 'Depósito',
    transferencia: 'Transferência', dinheiro: 'Dinheiro', checkout: 'Checkout',
  };

  // Resumo
  const resumo = {
    atraso: contas.filter(c => c.status === 'em_atraso').length,
    pendente: contas.filter(c => c.status === 'pendente').length,
    pago: contas.filter(c => c.status === 'pago').length,
    total: contas.reduce((s, c) => s + (c.valor_mensal || 0), 0),
    recebido: contas.filter(c => c.pago_este_mes).reduce((s, c) => s + (c.valor_mensal || 0), 0),
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando contas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex flex-wrap justify-between items-center gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Contas a Receber</h1>
            <p className="text-gray-500 text-sm mt-1">Controle de pagamentos das igrejas</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => loadData(mesSelecionado)} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-semibold text-sm">
              Atualizar
            </button>
            <button onClick={() => setCurrentView('gerenciar-igrejas')} className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 font-semibold text-sm">
              Voltar
            </button>
          </div>
        </div>

        {/* Mensagem */}
        {message.text && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {message.text}
          </div>
        )}

        {/* Cards Resumo */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white">
            <p className="text-xs opacity-90">Em Atraso</p>
            <p className="text-2xl font-bold">{resumo.atraso}</p>
          </div>
          <div className="bg-gradient-to-br from-amber-500 to-yellow-500 rounded-xl p-4 text-white">
            <p className="text-xs opacity-90">Pendente</p>
            <p className="text-2xl font-bold">{resumo.pendente}</p>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
            <p className="text-xs opacity-90">Pago</p>
            <p className="text-2xl font-bold">{resumo.pago}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
            <p className="text-xs opacity-90">A Receber</p>
            <p className="text-2xl font-bold">R$ {(resumo.total - resumo.recebido).toFixed(0)}</p>
          </div>
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white">
            <p className="text-xs opacity-90">Recebido</p>
            <p className="text-2xl font-bold">R$ {resumo.recebido.toFixed(0)}</p>
          </div>
        </div>

        {/* Seletor de Mês */}
        <div className="flex items-center justify-center gap-3 mb-6 bg-white rounded-xl shadow-sm p-3">
          <button
            onClick={() => setMesSelecionado(navegarMes(mesSelecionado, -1))}
            className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-bold text-lg"
          >
            &lsaquo;
          </button>
          <div className="text-center min-w-[180px]">
            <p className="text-lg font-bold text-gray-800">{getMesLabel(mesSelecionado)}</p>
            {mesSelecionado !== getMesAtual() && (
              <button
                onClick={() => setMesSelecionado(getMesAtual())}
                className="text-xs text-blue-500 hover:text-blue-700 underline"
              >
                Voltar para mês atual
              </button>
            )}
          </div>
          <button
            onClick={() => setMesSelecionado(navegarMes(mesSelecionado, 1))}
            className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-bold text-lg"
          >
            &rsaquo;
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 bg-gray-200 rounded-lg p-1">
          <button
            onClick={() => setTab('contas')}
            className={`flex-1 py-2 rounded-md text-sm font-semibold transition-colors ${tab === 'contas' ? 'bg-white text-gray-800 shadow' : 'text-gray-600 hover:text-gray-800'}`}
          >
            Contas ({contas.length})
          </button>
          <button
            onClick={() => setTab('historico')}
            className={`flex-1 py-2 rounded-md text-sm font-semibold transition-colors ${tab === 'historico' ? 'bg-white text-gray-800 shadow' : 'text-gray-600 hover:text-gray-800'}`}
          >
            Histórico ({historico.length})
          </button>
        </div>

        {/* Tab Contas */}
        {tab === 'contas' && (
          <div className="space-y-3">
            {contas.map((conta) => {
              const cfg = statusConfig[conta.status] || statusConfig.sem_vencimento;
              const isRegistrando = registrando === conta.igreja_id;

              return (
                <div key={conta.igreja_id} className={`bg-white rounded-xl border ${cfg.bg.includes('border') ? '' : 'border-gray-100'} shadow-sm overflow-hidden`}>
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={`w-3 h-3 rounded-full flex-shrink-0 ${cfg.badge}`}></div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-gray-800 truncate">{conta.nome}</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <span className="text-xs text-gray-500">
                            R$ {(conta.valor_mensal || 0).toFixed(2).replace('.', ',')}
                          </span>
                          {conta.dia_vencimento && (
                            <span className="text-xs text-gray-500">Venc. dia {conta.dia_vencimento}</span>
                          )}
                          {conta.ultimo_pagamento && (
                            <span className="text-xs text-gray-400">
                              Último: {new Date(conta.ultimo_pagamento.data).toLocaleDateString('pt-BR')}
                              {conta.ultimo_pagamento.metodo && ` (${metodoLabels[conta.ultimo_pagamento.metodo] || conta.ultimo_pagamento.metodo})`}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`px-2.5 py-1 text-xs font-bold rounded-full text-white ${cfg.badge}`}>
                        {cfg.label}
                        {conta.status === 'em_atraso' && conta.dias_info > 0 ? ` (${conta.dias_info}d)` : ''}
                        {conta.status === 'pendente' && conta.dias_info > 0 ? ` (${conta.dias_info}d)` : ''}
                      </span>
                      {!conta.pago_este_mes && (
                        <button
                          onClick={() => {
                            if (isRegistrando) {
                              setRegistrando(null);
                            } else {
                              setRegistrando(conta.igreja_id);
                              setFormManual({ metodo: 'manual', observacao: '', valor: String(conta.valor_mensal || '') });
                            }
                          }}
                          className="px-3 py-1.5 text-xs font-medium bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                        >
                          {isRegistrando ? 'Cancelar' : 'Registrar Pgto'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Form de registro manual */}
                  {isRegistrando && (
                    <div className="border-t border-gray-100 p-4 bg-green-50">
                      <p className="text-sm font-semibold text-gray-700 mb-3">Registrar Pagamento - {conta.nome}</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Valor (R$)</label>
                          <input
                            type="number" step="0.01" min="0"
                            value={formManual.valor}
                            onChange={(e) => setFormManual({ ...formManual, valor: e.target.value })}
                            className="w-full px-2 py-1.5 text-sm border rounded-md"
                            placeholder={String(conta.valor_mensal || '0')}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Método</label>
                          <select
                            value={formManual.metodo}
                            onChange={(e) => setFormManual({ ...formManual, metodo: e.target.value })}
                            className="w-full px-2 py-1.5 text-sm border rounded-md"
                          >
                            <option value="manual">Manual</option>
                            <option value="pix">PIX</option>
                            <option value="transferencia">Transferência</option>
                            <option value="deposito">Depósito</option>
                            <option value="dinheiro">Dinheiro</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Observação</label>
                          <input
                            type="text"
                            value={formManual.observacao}
                            onChange={(e) => setFormManual({ ...formManual, observacao: e.target.value })}
                            className="w-full px-2 py-1.5 text-sm border rounded-md"
                            placeholder="Opcional"
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            onClick={() => handleRegistrarPagamento(conta)}
                            className="w-full px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-semibold"
                          >
                            Confirmar
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {contas.length === 0 && (
              <div className="text-center py-8 text-gray-400">Nenhuma igreja paga cadastrada</div>
            )}
          </div>
        )}

        {/* Tab Histórico */}
        {tab === 'historico' && (
          <div>
            <div className="mb-3">
              <input
                type="text"
                value={filtroHistorico}
                onChange={(e) => setFiltroHistorico(e.target.value)}
                placeholder="Filtrar por nome da igreja..."
                className="w-full md:w-72 px-3 py-2 border rounded-lg text-sm"
              />
            </div>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-gray-600">
                      <th className="text-left px-4 py-3 font-semibold">Data</th>
                      <th className="text-left px-4 py-3 font-semibold">Igreja</th>
                      <th className="text-left px-4 py-3 font-semibold">Valor</th>
                      <th className="text-left px-4 py-3 font-semibold">Método</th>
                      <th className="text-left px-4 py-3 font-semibold">Ref.</th>
                      <th className="text-left px-4 py-3 font-semibold">Obs.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historico
                      .filter(h => !filtroHistorico || h.igreja_nome?.toLowerCase().includes(filtroHistorico.toLowerCase()))
                      .map((h) => (
                        <tr key={h.id} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-600">
                            {new Date(h.data_pagamento).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-800">{h.igreja_nome}</td>
                          <td className="px-4 py-3 text-gray-700">
                            R$ {(h.valor || 0).toFixed(2).replace('.', ',')}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              h.metodo === 'pix' ? 'bg-cyan-100 text-cyan-700' :
                              h.metodo === 'cartao' ? 'bg-purple-100 text-purple-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {metodoLabels[h.metodo] || h.metodo}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-500">{h.referencia_mes || '-'}</td>
                          <td className="px-4 py-3 text-gray-400 text-xs">{h.observacao || '-'}</td>
                        </tr>
                      ))}
                    {historico.length === 0 && (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-gray-400">Nenhum pagamento registrado</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContasReceber;
